import { env } from "@/web/lib/env";
import type { CanonicalAddress } from "@tycho/web-bindings/types";

/** A geocoded point. `lon`/`lat` in WGS84 degrees. */
export type Coordinates = { latitude: number; longitude: number };

function addressQueryString(address: CanonicalAddress): string {
  const line2 = address.addressLine2 ? ` ${address.addressLine2}` : "";
  return `${address.addressLine1}${line2}, ${address.city}, ${address.state} ${address.zip}`;
}

/**
 * Forward-geocode a single address via Mapbox, returning its coordinates or
 * `null` if Mapbox can't resolve it.
 *
 * This is the app's one geocoding entry point. Coordinates are meant to be
 * resolved *once* — at object creation (so the row carries its own lat/long)
 * or via the admin backfill tool — never on every map render. The shared `Map`
 * component takes coordinates directly and does not geocode.
 */
export async function geocodeAddress(address: CanonicalAddress): Promise<Coordinates | null> {
  const url = new URL("https://api.mapbox.com/search/geocode/v6/forward");
  url.searchParams.set("q", addressQueryString(address));
  url.searchParams.set("country", address.countryCode || "US");
  url.searchParams.set("limit", "1");
  url.searchParams.set("access_token", env.VITE_MAPBOX_TOKEN);

  const res = await fetch(url.toString());
  if (!res.ok) return null;
  const data = (await res.json()) as {
    features?: Array<{ geometry?: { coordinates?: [number, number] } }>;
  };
  const coords = data.features?.[0]?.geometry?.coordinates;
  if (!coords) return null;
  return { longitude: coords[0], latitude: coords[1] };
}

/** A single street-address suggestion from {@link searchAddresses}. */
export type AddressSuggestion = {
  /** Mapbox feature id — stable key for the suggestion. */
  id: string;
  /** Full formatted address, e.g. "123 Main St, Catskill, New York 12414". */
  fullAddress: string;
  /** Street line, e.g. "123 Main St". */
  addressLine1: string;
  city: string;
  state: string;
  zip: string;
  countryCode: string;
  latitude: number;
  longitude: number;
};

type MapboxAddressContext = {
  region?: { region_code?: string; name?: string };
  postcode?: { name?: string };
  place?: { name?: string };
  locality?: { name?: string };
  country?: { country_code?: string };
};

type MapboxAddressFeature = {
  id?: string;
  geometry?: { coordinates?: [number, number] };
  properties?: {
    mapbox_id?: string;
    full_address?: string;
    name?: string;
    context?: MapboxAddressContext;
  };
};

/**
 * Forward-geocode a free-text query to street-address suggestions via Mapbox,
 * restricted to `address`-type features (we deliberately do NOT want POI/place
 * search here — the user is targeting a specific building). Returns up to
 * `limit` resolved suggestions, each carrying both the parsed address parts and
 * coordinates so callers can plot + persist without a second round-trip.
 */
export async function searchAddresses(
  query: string,
  { limit = 5, signal }: { limit?: number; signal?: AbortSignal } = {},
): Promise<AddressSuggestion[]> {
  const trimmed = query.trim();
  if (trimmed.length === 0) return [];

  const url = new URL("https://api.mapbox.com/search/geocode/v6/forward");
  url.searchParams.set("q", trimmed);
  url.searchParams.set("types", "address");
  url.searchParams.set("autocomplete", "true");
  url.searchParams.set("limit", String(limit));
  url.searchParams.set("access_token", env.VITE_MAPBOX_TOKEN);

  const res = await fetch(url.toString(), { signal });
  if (!res.ok) return [];
  const data = (await res.json()) as { features?: MapboxAddressFeature[] };

  return (data.features ?? []).flatMap((feature): AddressSuggestion[] => {
    const coords = feature.geometry?.coordinates;
    const props = feature.properties;
    if (!coords || !props) return [];
    const ctx = props.context ?? {};
    return [
      {
        id: props.mapbox_id ?? feature.id ?? `${coords[0]},${coords[1]}`,
        fullAddress: props.full_address ?? props.name ?? trimmed,
        addressLine1: props.name ?? "",
        city: ctx.place?.name ?? ctx.locality?.name ?? "",
        state: ctx.region?.region_code ?? ctx.region?.name ?? "",
        zip: ctx.postcode?.name ?? "",
        countryCode: (ctx.country?.country_code ?? "US").toUpperCase(),
        longitude: coords[0],
        latitude: coords[1],
      },
    ];
  });
}
